import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { PPEData } from '../types/ppe';

interface CategoryPageProps {
  data: PPEData;
}

const CategoryPage: React.FC<CategoryPageProps> = ({ data }) => {
  const { categoryId } = useParams<{ categoryId: string }>();
  
  const category = data.categories.find(cat => cat.id === categoryId);
  
  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">Category Not Found</h1>
          <Link to="/" className="text-brand-yellow hover:text-primary-600 transition-colors duration-200">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-brand-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <Link to="/" className="hover:text-brand-yellow transition-colors duration-200">Home</Link>
            <span>/</span>
            <span className="text-black font-medium">Categories</span>
            <span>/</span>
            <span className="text-black font-medium">{category.name}</span>
          </nav>
          
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-brand-yellow rounded-lg flex items-center justify-center overflow-hidden">
              {category.certificates.length > 0 && 
               category.certificates[0].products && 
               category.certificates[0].products.length > 0 ? (
                <img 
                  src={category.certificates[0].products[0].imageUrl} 
                  alt={category.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-black font-bold text-2xl">
                  {category.name.charAt(0)}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-4xl font-bold text-black mb-2">{category.name}</h1>
              <p className="text-lg text-gray-600 max-w-2xl">{category.description}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-black mb-2">
            {category.certificates.length} Certificate{category.certificates.length !== 1 ? 's' : ''} Available
          </h2>
          <p className="text-gray-600">
            Browse all available certificates for {category.name.toLowerCase()} protection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {category.certificates.map((certificate) => (
            <Link
              key={certificate.id}
              to={`/certificate/${certificate.id}`}
              className="card hover:scale-105 transition-transform duration-200 group"
            >
              <div className="mb-4">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-base font-medium bg-[#1B559C] text-white mb-3">
                  {certificate.code}
                </div>
                <h3 className="text-xl font-bold text-black mb-2 group-hover:text-brand-yellow transition-colors duration-200">
                  {certificate.name}
                </h3>
                <p className="text-gray-600 mb-4">{certificate.description}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    certificate.riskLevel === 'I' ? 'bg-green-500' :
                    certificate.riskLevel === 'II' ? 'bg-yellow-500' :
                    certificate.riskLevel === 'III' ? 'bg-red-500' : 'bg-gray-500'
                  }`}></div>
                  <span className="text-sm text-gray-500">Risk Level {certificate.riskLevel}</span>
                </div>
                <div className="text-brand-yellow font-medium group-hover:text-primary-600 transition-colors duration-200">
                  View Details →
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Back to Categories */}
        <div className="mt-12 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-brand-yellow hover:text-primary-600 transition-colors duration-200 font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to All Categories</span>
          </Link>
        </div>
      </div>

      {/* Powered by Section */}
      <div className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3">
            <span className="text-gray-400">PPE Certificate Portal powered by</span>
            <img 
              src="/sws-logo.png" 
              alt="Safety Workwear Shop" 
              className="h-8 w-auto opacity-80"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
